import type { Steuerjahr, Takt, TaktZeile } from "./typen";

const iso = (d: Date) => d.toISOString().slice(0, 10);
const tag = (j: number, m: number, t: number) =>
  new Date(Date.UTC(j, m - 1, t));

// Ostersonntag nach Gauß/Meeus
const ostern = (j: number) => {
  const a = j % 19;
  const b = Math.floor(j / 100);
  const c = j % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monat = Math.floor((h + l - 7 * m + 114) / 31);
  const t = ((h + l - 7 * m + 114) % 31) + 1;
  return tag(j, monat, t);
};

const verschoben = (d: Date, tage: number) =>
  new Date(d.getTime() + tage * 86400000);

export const feiertageBerlin = (j: number) => {
  const o = ostern(j);
  return new Set(
    [
      tag(j, 1, 1),
      tag(j, 3, 8),
      verschoben(o, -2),
      verschoben(o, 1),
      tag(j, 5, 1),
      verschoben(o, 39),
      verschoben(o, 50),
      tag(j, 10, 3),
      tag(j, 12, 25),
      tag(j, 12, 26),
    ].map(iso),
  );
};

export const naechsterWerktag = (datum: string) => {
  let d = new Date(`${datum}T00:00:00Z`);
  while (
    [0, 6].includes(d.getUTCDay()) ||
    feiertageBerlin(d.getUTCFullYear()).has(iso(d))
  )
    d = verschoben(d, 1);
  return iso(d);
};

// Der Zeitraum endet in `monat`: fällig am 10. des Folgemonats, mit Dauerfristverlängerung einen Monat später.
export const fristNach = (jahr: number, monat: number, dauerfrist: boolean) =>
  naechsterWerktag(iso(tag(jahr, monat + (dauerfrist ? 2 : 1), 10)));

export const taktAm = (zeilen: TaktZeile[], datum: string): Takt =>
  [...zeilen]
    .sort((a, b) => a.gueltig_ab.localeCompare(b.gueltig_ab))
    .filter((z) => z.gueltig_ab <= datum)
    .pop()?.takt ?? "offen";

export type Zeitraum = {
  id: string;
  name: string;
  art: "monat" | "quartal";
  nummer: number;
  frist: string;
  offen: boolean; // Takt offen: Monat und Quartal stehen gleichwertig
};

const MONATE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

export const zeitraeume = (
  steuerjahr: Steuerjahr,
  takte: TaktZeile[],
): Zeitraum[] => {
  const j = steuerjahr.jahr;
  const liste: Zeitraum[] = [];
  const beginn = steuerjahr.taetigkeitsbeginn ?? `${j}-01-01`;
  const ersterTag = (m: number) => iso(tag(j, m, 1));
  const letzterTag = (m: number) => iso(tag(j, m + 1, 0));
  for (let m = 1; m <= 12; m++) {
    const takt = taktAm(takte, ersterTag(m));
    if ((takt === "monatlich" || takt === "offen") && letzterTag(m) >= beginn) {
      liste.push({
        id: `m${m}`,
        name: `${MONATE[m - 1]} ${j}`,
        art: "monat",
        nummer: m,
        frist: fristNach(j, m, steuerjahr.dauerfrist),
        offen: takt === "offen",
      });
    }
    if (m % 3 === 0) {
      const q = m / 3;
      const takt3 = taktAm(takte, ersterTag(m - 2));
      if (
        (takt3 === "vierteljaehrlich" || takt3 === "offen") &&
        letzterTag(m) >= beginn
      ) {
        liste.push({
          id: `q${q}`,
          name: `${q}. Quartal ${j}`,
          art: "quartal",
          nummer: q,
          frist: fristNach(j, m, steuerjahr.dauerfrist),
          offen: takt3 === "offen",
        });
      }
    }
  }
  return liste.sort(
    (a, b) => a.frist.localeCompare(b.frist) || a.art.localeCompare(b.art),
  );
};

export const naechsteTermine = (
  liste: Zeitraum[],
  heute: string,
  anzahl: number,
) => {
  const kommend = liste.filter((z) => z.frist >= heute);
  const grenze = kommend[anzahl - 1]?.frist;
  return kommend.filter((z) => !grenze || z.frist <= grenze);
};
